<template>
  <div class="summaries-per-task">
    <div class="columns">
      <div class="column is-full">
        <!-- header -->
        <div v-if="!isLoading" style="margin-bottom: 25px;" class="notification">
          <div class="columns">
            <div class="column is-3"> <!-- Title -->
              <p class="has-text-weight-bold is-size-4">{{ $t('grouped-logs.project-tasks-title') }}</p>
            </div>
            <div class="column is-narrow">
              <p>
                <span class="has-text-weight-bold is-size-5">
                  {{
                    (this.groupedLogsSum.expected_total_fee) !== undefined ? toFixedIfNecessary(this.groupedLogsSum.expected_total_fee) : 0
                  }}
                </span> {{ defaultValues.priceUnit }} <span class="ml-3">{{
                  $t('grouped-logs.expected-total-fee-title')
                }}</span>
              </p>
            </div>
            <div class="column is-6"></div>
          </div>
          <div class="columns"> <!-- Parent divider -->
            <div class="column is-7">
              <div class="columns"> <!-- budget sum -->
                <div class="column">
                  <p><span class="has-text-weight-bold is-size-5 internal-text">
                    {{
                      (this.groupedLogsSum.expected_executed_fee) !== undefined ? toFixedIfNecessary(this.groupedLogsSum.expected_executed_fee) : 0
                    }}</span> {{ defaultValues.priceUnit }} 
                    <span class="ml-3">{{ $t('grouped-logs.expected-executed-fee-title') }}</span>
                    ({{ this.groupedLogsSum.progress_percentage_work }}%)
                  </p>
                  <p><span class="has-text-weight-bold is-size-5 internal-text">
                    {{
                      (this.groupedLogsSum.executed_fee) !== undefined ? toFixedIfNecessary(this.groupedLogsSum.executed_fee) : 0
                    }}</span> {{ defaultValues.priceUnit }} 
                    <span class="ml-3">{{
                      $t('grouped-logs.executed-fee-title')
                    }}</span>
                    ({{ this.groupedLogsSum.progress_percentage_budget }}%)
                  </p>
                </div>
              </div>
              <div class="columns"> <!-- profit sum -->
                <div class="column">
                  <p><span class="has-text-weight-bold is-size-5 internal-text"
                           :style="profitTextStyle(this.groupedLogsSum.profit)">
                    {{ toFixedIfNecessary(nullToZero(this.groupedLogsSum.profit)) }} </span> 
                    {{ defaultValues.priceUnit }} 
                    <span class="ml-3">{{ $t('grouped-logs.profit-title') }}</span>
                  </p>
                  <p><span class="has-text-weight-bold is-size-5 internal-text"
                           :style="profitTextStyle(this.groupedLogsSum.expected_end_profit)">
                           {{ toFixedIfNecessary(nullToZero(this.groupedLogsSum.expected_end_profit)) }}</span> 
                           {{ defaultValues.priceUnit }} 
                      <span class="ml-3">{{ $t('grouped-logs.expected-end-profit-title') }}</span>
                  </p>
                </div>
              </div>
            </div>
            <div class="column is-5"> <!-- progress sum -->
              <div class="columns">
                <div class="column"> <!-- quantity -->
                  <p>{{ $t('grouped-logs.progress-percentage-quantity-title') }} <span
                      class="has-text-weight-bold is-size-5 ml-2">{{
                      toFixedIfNecessary(this.groupedLogsSum.progress_percentage_work, 1)
                    }}%</span>
                  </p>
                  <b-progress v-if="groupedLogsSum.progress_percentage_work <= 100" :max="100" size="is-medium"
                              style="width: 90%">
                    <template #bar>
                      <b-progress-bar :value="parseFloat(groupedLogsSum.progress_percentage_work)"
                                      type="is-primary"></b-progress-bar>
                    </template>
                  </b-progress>
                  <b-progress v-else :max="100" size="is-medium">
                    <template #bar>
                      <b-progress-bar :value="90" type="is-primary"></b-progress-bar>
                      <b-progress-bar :value="10" show-value>...</b-progress-bar>
                    </template>
                  </b-progress>
                </div>
              </div>
              <div class="columns">
                <div class="column"> <!-- budget -->
                  <p>{{ $t('grouped-logs.progress-percentage-fee-title') }} <span
                      class="has-text-weight-bold is-size-5 ml-2">{{
                      toFixedIfNecessary(this.groupedLogsSum.progress_percentage_budget, 1)
                    }}%</span></p>
                  <b-progress v-if="groupedLogsSum.progress_percentage_budget <= 100" :max="100" size="is-medium"
                              style="width: 90%">
                    <template #bar>
                      <b-progress-bar :value="parseFloat(groupedLogsSum.progress_percentage_budget)"
                                      :type="groupedLogsSum.profit >= 0 ? 'is-success' : 'is-danger'"></b-progress-bar>
                    </template>
                  </b-progress>
                  <b-progress v-else :max="100" size="is-medium">
                    <template #bar>
                      <b-progress-bar :value="90"
                                      :type="groupedLogsSum.profit >= 0 ? 'is-success' : 'is-danger'"></b-progress-bar>
                      <b-progress-bar :value="10" show-value>...</b-progress-bar>
                    </template>
                  </b-progress>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- table -->
        <b-table class="summaries-table" :data="groupedLogsData" :hoverable="true" bordered
                 :mobile-cards="false" :loading="isLoading" :scrollable="true"
                 :show-header="false">
          <b-table-column field="title" :label="this.$t('grouped-logs.task-title')"
                          v-slot="props">
            <div style="margin-top: 8px; margin-bottom: 8px;">
              <div class="columns">
                <div class="column has-text-centered is-narrow"> <!-- Title -->
                  <p class="has-text-weight-bold is-size-5">{{ props.row.title }}</p>
                </div>
              </div>
              <div class="columns"> <!-- Parent divider -->
                <div class="column is-7">
                  <div class="columns"> <!-- quantity and budget -->
                    <div class="column is-half">
                      <p><span class="has-text-weight-bold is-size-5">{{ toFixedIfNecessary(props.row.logged_quantities) }}</span>
                        {{ getTranslatedQuantityUnit(props.row.unit) }} <span class="ml-3">{{ $t('grouped-logs.logged-quantities-title') }}</span>
                      </p>
                      <p><span class="has-text-weight-bold is-size-5">{{ toFixedIfNecessary(nullToZero(props.row.executed_performance)) }}</span>
                        {{ getPerformanceUnit(props.row.unit) }}<span class="ml-3">{{ $t('grouped-logs.executed-performance') }}</span>
                      </p>
                    </div>
                    <div class="column is-half">
                      <p><span class="has-text-weight-bold is-size-5">{{ toFixedIfNecessary(props.row.expected_quantity) }}</span>
                        {{ getTranslatedQuantityUnit(props.row.unit) }} <span class="ml-3">{{ $t('grouped-logs.quantity-title') }}</span>
                      </p>
                      <p><span class="has-text-weight-bold is-size-5">{{ toFixedIfNecessary(nullToZero(props.row.expected_performance)) }}</span>
                        {{ getPerformanceUnit(props.row.unit) }}<span class="ml-3">{{ $t('grouped-logs.expected-performance') }}</span>
                      </p>
                      <p><span class="has-text-weight-bold is-size-5">{{
                              toFixedIfNecessary(nullToZero(props.row.expected_executed_fee))
                        }}</span> {{ defaultValues.priceUnit }} <span
                          class="ml-3">{{ $t('grouped-logs.expected-executed-fee-title') }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column">
                      <p><span class="has-text-weight-bold is-size-5">{{
                          toFixedIfNecessary(props.row.executed_fee)
                        }}</span>
                        {{ defaultValues.priceUnit }} <span class="ml-3">{{
                            $t('grouped-logs.executed-fee-title')
                          }}</span>
                      </p>
                    </div>
                    <div class="column">
                      <p><span class="has-text-weight-bold is-size-5">{{
                          toFixedIfNecessary(props.row.expected_total_fee)
                        }}</span>
                        {{ defaultValues.priceUnit }} <span class="ml-3">{{
                            $t('grouped-logs.expected-total-fee-title')
                          }}</span>
                      </p>
                    </div>
                  </div>
                  <div class="columns" style="margin-top: -30px"> <!-- personnel worked duration nad used budget -->
                    <div class="column is-half">
                      <p><span class="has-text-weight-bold is-size-5 internal-text">{{
                          getFormattedTime(props.row.logged_internal_durations)
                        }}</span> {{ $t("enums.duration-unit-short.hour") }}
                        <span class="ml-3">{{ $t('grouped-logs.logged-internal-duration-title') }}</span>
                        ({{ toFixedIfNecessary(props.row.executed_internal_fee) }} {{ defaultValues.priceUnit }})
                      </p>
                      <p><span class="has-text-weight-bold is-size-5 external-text">{{
                          getFormattedTime(props.row.logged_external_durations)
                        }}</span> {{ $t("enums.duration-unit-short.hour") }}
                        <span class="ml-3">{{ $t('grouped-logs.logged-external-duration-title') }}</span>
                        ({{ toFixedIfNecessary(props.row.executed_external_fee) }} {{ defaultValues.priceUnit }})
                      </p>
                    </div>
                    <div class="column is-half">
                      <p><span class="has-text-weight-bold is-size-5 internal-text">{{
                          getFormattedTime(props.row.expected_internal_hours)
                        }}</span> {{ $t("enums.duration-unit-short.hour") }}
                        <span class="ml-3">{{ $t('grouped-logs.expected-internal-duration-title') }}</span>
                        ({{ (props.row.expected_internal_hours * props.row.internal_personal_cost).toFixed(0) }}
                        {{ defaultValues.priceUnit }})</p>
                      <p><span class="has-text-weight-bold is-size-5 external-text">{{
                          getFormattedTime(props.row.expected_external_hours)
                        }}</span> {{ $t("enums.duration-unit-short.hour") }}
                        <span class="ml-3">{{ $t('grouped-logs.expected-external-duration-title') }}</span>
                        ({{ (props.row.expected_external_hours * props.row.external_personal_cost).toFixed(0) }}
                        {{ defaultValues.priceUnit }})</p>
                    </div>
                  </div>
                  <div class="columns"> <!-- summary -->
                    <div class="column">
                      <p><span class="has-text-weight-bold is-size-5"
                               :style="profitTextStyle(props.row.profit)">{{
                          (props.row.profit) !== null ? toFixedIfNecessary(props.row.profit) : 0
                        }}</span>
                        {{ defaultValues.priceUnit }} <span class="ml-3">{{ $t('grouped-logs.profit-title') }}</span>
                      </p>
                      <p><span class="has-text-weight-bold is-size-5"
                               :style="profitTextStyle(props.row.expected_end_profit)">{{
                          (props.row.expected_end_profit) !== null ? toFixedIfNecessary(props.row.expected_end_profit) : 0
                        }}</span> {{ defaultValues.priceUnit }} <span
                          class="ml-3">{{ $t('grouped-logs.expected-end-profit-title') }}</span></p>
                    </div>
                  </div>
                </div>
                <div class="column is-5"> <!-- progress -->
                  <div class="columns">
                    <div class="column"> <!-- quantity -->
                      <p>{{ $t('grouped-logs.progress-percentage-quantity-title') }} <span
                          class="has-text-weight-bold is-size-5 ml-2">{{
                          (props.row.progress_percentage_quantity) !== null ?
                              toFixedIfNecessary(props.row.progress_percentage_quantity, 1) : 0
                        }}%</span></p>
                      <b-progress v-if="props.row.progress_percentage_quantity <= 100" :max="100" size="is-medium"
                                  style="width: 90%">
                        <template #bar>
                          <b-progress-bar :value="props.row.progress_percentage_quantity"
                                          type="is-primary"></b-progress-bar>
                        </template>
                      </b-progress>
                      <b-progress v-else :max="100" size="is-medium">
                        <template #bar>
                          <b-progress-bar :value="90" type="is-primary"></b-progress-bar>
                          <b-progress-bar :value="10" show-value>...</b-progress-bar>
                        </template>
                      </b-progress>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column"> <!-- budget -->
                      <p>{{ $t('grouped-logs.progress-percentage-fee-title') }} <span
                          class="has-text-weight-bold is-size-5 ml-2">{{
                          (props.row.progress_percentage_fee) != null ?
                              toFixedIfNecessary(props.row.progress_percentage_fee, 1) : 0
                        }}%</span></p>
                      <b-progress v-if="props.row.progress_percentage_fee <= 100" :max="100" size="is-medium"
                                  style="width: 90%">
                        <template #bar>
                          <b-progress-bar :value="props.row.progress_percentage_fee"
                                          :type="props.row.profit >= 0 ? 'is-success' : 'is-danger'"></b-progress-bar>
                        </template>
                      </b-progress>
                      <b-progress v-else :max="100" size="is-medium">
                        <template #bar>
                          <b-progress-bar :value="90"
                                          :type="props.row.profit >= 0 ? 'is-success' : 'is-danger'"></b-progress-bar>
                          <b-progress-bar :value="10" show-value>...</b-progress-bar>
                        </template>
                      </b-progress>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </b-table-column>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: "GroupedLogsPerTask",
  components: {},
  props: {
    'project_id': {
      type: String,
    }
  },
  data() {
    return {
      isLoading: false,
      groupedLogsData: [],
      groupedLogsSum: {
        progress_percentage_work: 0,
        progress_percentage_budget: 0
      },
    }
  },
  methods: {
    readProjectSummaryByTask: function () {
      this.isLoading = true
      this.groupedLogsData = []
      this.groupedLogsSum = []
      const functions = firebase.app().functions("europe-west3");
      const getProjectSummaryFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getProjectSummaryFunction({action: "get_project_summary_by_task", document_id: this.project_id, fill_meta: true})
          .then((result) => {
            this.isLoading = false
            this.groupedLogsData = result.data["grouped_tasks"]
            this.groupedLogsSum = result.data["sum"]
            if (this.groupedLogsSum.expected_total_fee !== 0) {
              this.groupedLogsSum.progress_percentage_work = ((this.groupedLogsSum.expected_executed_fee / this.groupedLogsSum.expected_total_fee) * 100).toFixed(2)
              this.groupedLogsSum.progress_percentage_budget = ((this.groupedLogsSum.executed_fee / this.groupedLogsSum.expected_total_fee) * 100).toFixed(2)
            }
            console.log("get_project_summary_by_task result: ", result.data);
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message)
            console.log("Error get_project_summary_by_task: ", error);
          });
    },
    nullToZero: function(value) {
      if (value == null || value === "" || value === undefined) 
        return 0 
      else
        return value    
    }
  },
  mounted() {
    this.readProjectSummaryByTask();
  }
};
</script>

<style scoped>
.summaries-table {
  margin: 0;
}
</style>
